/**
 * Recovery Trauma™ — client-side problem reporting.
 *
 * Two entry points:
 *  - `submitProblemReport` — a member describing something that went wrong.
 *  - `installGlobalErrorReporting` — automatic capture of genuine crashes.
 *
 * The automatic capture is intentionally strict: harmless console warnings,
 * cancelled requests, browser-extension errors and offline blips are all
 * dropped, and at most a handful of reports are sent per browsing session.
 */

import { reportIssue } from "@/lib/issues.functions";

/** Reports allowed per page-session from the automatic capture. */
const MAX_AUTO_REPORTS_PER_SESSION = 5;
let autoReportsSent = 0;
const seenThisSession = new Set<string>();

function deviceContext() {
  if (typeof window === "undefined") return {};
  const nav = window.navigator;
  const isNative =
    typeof (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform ===
    "function"
      ? Boolean((window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor!.isNativePlatform!())
      : false;
  return {
    pagePath: window.location.pathname + window.location.search,
    userAgent: nav.userAgent,
    platform: isNative ? "iOS app" : "Website",
    appVersion: (import.meta.env["VITE_APP_VERSION"] as string | undefined) ?? undefined,
    context: {
      viewport: `${window.innerWidth}×${window.innerHeight}`,
      language: nav.language,
      online: nav.onLine,
      referrer: document.referrer || undefined,
    } as Record<string, unknown>,
  };
}

export interface ProblemReport {
  /** The member's own words. */
  description: string;
  /** Optional feature area the member picked. */
  feature?: string;
}

export async function submitProblemReport(report: ProblemReport): Promise<void> {
  const ctx = deviceContext();
  await reportIssue({
    data: {
      source: "user_report",
      severity: "error",
      title: report.description.slice(0, 120).replace(/\s+/g, " ").trim(),
      detail: report.description,
      feature: report.feature,
      ...ctx,
    },
  });
}

/**
 * Report a failure detected in app code (a practice that wouldn't play, an AI
 * reflection that failed, a failed upload). Safe to call anywhere — it never
 * throws and never blocks.
 */
export function reportFeatureFailure(args: {
  source: "client_error" | "audio" | "ai" | "upload" | "auth" | "payment" | "other";
  title: string;
  detail?: string;
  feature?: string;
  severity?: "critical" | "error" | "warning";
  context?: Record<string, unknown>;
}): void {
  if (typeof window === "undefined") return;
  const key = `${args.source}:${args.title}`;
  if (seenThisSession.has(key)) return;
  seenThisSession.add(key);
  if (autoReportsSent >= MAX_AUTO_REPORTS_PER_SESSION) return;
  autoReportsSent += 1;
  const ctx = deviceContext();
  void reportIssue({
    data: {
      source: args.source,
      severity: args.severity ?? "error",
      title: args.title,
      detail: args.detail,
      feature: args.feature,
      ...ctx,
      context: { ...(ctx.context ?? {}), ...(args.context ?? {}) },
    },
  }).catch(() => {
    /* reporting must never surface to the member */
  });
}

function describeError(value: unknown): { title: string; detail: string } | null {
  if (value instanceof Error) {
    return {
      title: `${value.name}: ${value.message}`.slice(0, 200),
      detail: value.stack ?? value.message,
    };
  }
  if (typeof value === "string" && value.trim()) {
    return { title: value.slice(0, 200), detail: value };
  }
  return null;
}

/**
 * Install the global crash capture. Only real, unhandled JavaScript errors
 * reach the admin inbox, and only after the noise filter on the server.
 */
export function installGlobalErrorReporting(): () => void {
  if (typeof window === "undefined") return () => {};

  const handle = (value: unknown, kind: "error" | "unhandledrejection") => {
    const described = describeError(value);
    if (!described) return;
    reportFeatureFailure({
      source: "client_error",
      title: described.title,
      detail: described.detail,
      feature: window.location.pathname,
      context: { kind },
    });
  };

  const onError = (e: ErrorEvent) => handle(e.error ?? e.message, "error");
  const onRejection = (e: PromiseRejectionEvent) => handle(e.reason, "unhandledrejection");

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
